import { Injectable, Inject, NgZone } from '@angular/core';
import {
  HttpClient,
  HttpEvent,
  HttpHandler,
  HttpHeaders,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import {
  Observable,
  of,
  from,
  Subscription,
  Subject,
  timer,
  firstValueFrom,
  filter,
  finalize,
  BehaviorSubject,
  switchMap,
  throwError,
  timeout,
} from 'rxjs';
import {concatMap, mergeMap, take, shareReplay} from 'rxjs/operators';
import {catchError, map, retryWhen} from 'rxjs/operators';
import {SETTINGS_SERVICE, SettingsService, ZITI_DOMAIN_CONTROLLER} from 'ziti-console-lib';
import { HTTP_CLIENT, LoggerService, FeatureService, MenuService } from '@netfoundry-ui/shared/services';
import {
  ZitiWasmService,
  serializeHttpRequest,
  parseRawHttpResponse,
  ZitiConnectionStatusComponent,
  ConnectionStep,
  ZITI_CONNECTION_TITLE_ID,
} from '@netfoundry-ui/shared/ziti-wasm';
import { Auth0Service } from '@netfoundry-ui/shared/authorization';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { NetworkServiceV2, ZitiControllerService } from "@netfoundry-ui/shared/apiv2";
import { MatDialog, MatDialogRef } from '@angular/material/dialog';

import { isEmpty, isEqual } from 'lodash-es';
import {NETWORK_SERVICE} from "./network.service";

// Longer than the SDK's own wait for an edge router (about 11 seconds), so a slow first dial can still pass
const OVERLAY_PROBE_TIMEOUT_MS = 15000;

// A 401 right after a fresh session means the controller rejects new sessions too; minting again would loop
const SESSION_REMINT_COOLDOWN_MS = 30000;

// The apps that show the connection indicator, and so the connection dialog
const NETWORK_APP_AREAS = ['Network', 'ZAC', 'Support_Operations'];

export class AsyncMutex {
  private mutex = Promise.resolve();

  lock<T>(fn: () => Promise<T>): Promise<T> {
    const run = async () => {
      /* eslint-disable-next-line no-useless-catch */
      try {
        return await fn();
      } catch (err) {
        throw err;
      }
    };

    // Chain onto the previous mutex
    const current = this.mutex;
    let release: () => void;
    this.mutex = new Promise<void>(resolve => (release = resolve));

    return current.then(() => run()).finally(() => {if (release) release()});
  }

  async runExclusive<T>(fn: () => Promise<T>): Promise<T> {
    return this.lock(fn);
  }
}

export class HttpQueueManager {
  private queue$ = new Subject<Observable<HttpEvent<any>>>();
  private allIdle$ = new BehaviorSubject<boolean>(true);
  private inFlight = 0;

  constructor(private logger: any) {
    this.queue$.subscribe(queued$ => {
      this.inFlight++;
      this.allIdle$.next(false);

      queued$.pipe(
        finalize(() => {
          this.inFlight--;
          this.logger.info?.(`over-Ziti HTTP Request finished. Current in-flight count: ${this.inFlight}`);
          if (this.inFlight === 0) {
            this.allIdle$.next(true);
          }
        })
      ).subscribe({
        next: () => {},
        error: () => {}, // handled in queued$ itself
        complete: () => {}
      });
    });
  }

  // Queue a deferred request observable
  enqueue(req$: Observable<HttpEvent<any>>): void {
    this.queue$.next(req$);
  }

  // Wait until all currently in-flight requests are done
  async waitForAllInFlight(): Promise<void> {
    if (this.inFlight === 0) return;
    await firstValueFrom(this.allIdle$.pipe(filter(idle => idle)));
  }
}

const SENSITIVE_HEADER_PATTERN = /^(authorization|proxy-authorization|cookie|set-cookie|x-api-key|x-auth-token|x-access-token|zt-session)$/i;

function redactHeaders(headers: Record<string, any> | undefined | null): Record<string, any> {
  if (!headers) return {};
  const out: Record<string, any> = {};
  for (const [k, v] of Object.entries(headers)) {
    out[k] = SENSITIVE_HEADER_PATTERN.test(k) ? '[redacted]' : v;
  }
  return out;
}

function angularHeadersToObject(headers: any): Record<string, string> {
  if (!headers || typeof headers.keys !== 'function') return {};
  const out: Record<string, string> = {};
  for (const k of headers.keys()) {
    out[k] = headers.get(k);
  }
  return out;
}

@Injectable({
  providedIn: 'root',
})
export class ZitiApiInterceptor implements HttpInterceptor {

  private wasmFailureEncountered = false;
  private currentWasmControllerHost = '';
  private overlayActive = false;
  private overlayCancelled = false;
  // A failed or cancelled overlay stays on REST until the network or the overlay setting changes
  private overlayFailed = false;
  private overlayDomain = '';
  // Only a session announced by zitiSettings starts the overlay; one restored from storage or still being set gets replaced
  private overlaySessionId = '';
  private lastSessionRemintAt = 0;
  subscription = new Subscription();
  private queue$ = new Subject<Observable<HttpEvent<any>>>();
  private mutex = new AsyncMutex();
  private httpQueueManager;
  private statusDialogRef: MatDialogRef<ZitiConnectionStatusComponent> | null = null;

  constructor(
    @Inject(SETTINGS_SERVICE) private settingsService: SettingsService,
    @Inject(ZITI_DOMAIN_CONTROLLER) public zitiControllerService: ZitiControllerService,
    @Inject(HTTP_CLIENT)private httpClient: HttpClient,
    private logger: LoggerService,
    private ngZone: NgZone,
    @Inject(NETWORK_SERVICE) public networkService: NetworkServiceV2,
    private featureService: FeatureService,
    private zitiWasmService: ZitiWasmService,
    private dialogForm: MatDialog,
    private auth0Service: Auth0Service,
    private menuService: MenuService
  ) {
    // Serially process requests that hit the mgmt API in a queue
    this.queue$
      .pipe(concatMap(obs$ => obs$))
      .subscribe(); // just kicks off the processing

    // Re-init the WASM context any time the selected network changes in the UI
    this.subscription.add(
      this.zitiControllerService.zitiSettings.subscribe( async (settings) => {
        this.overlaySessionId = settings.zitiSessionId || '';
        if (isEmpty(settings.zitiDomain)) {
          return;
        }
        // A new session restarts a working overlay so it uses the new token, but not a failed or cancelled one
        if (settings.zitiDomain === this.overlayDomain && (this.overlayFailed || this.overlayCancelled)) {
          return;
        }
        this.overlayDomain = settings.zitiDomain;
        await this.disconnectOverlay();
        this.syncOverlay();
      })
    );
    this.subscription.add(this.zitiWasmService.retryRequested$.subscribe(() => this.retryOverlay()));

    this.httpQueueManager = new HttpQueueManager(this.logger);
  }

  private async doAsyncWork( req: HttpRequest<any> ): Promise<HttpResponse<any>> {

    let resp = new HttpResponse();

    await this._initZitiWasm(); // no-op if already initialized

    /* eslint-disable-next-line no-useless-catch */
    try {
      resp = await Promise.resolve().then(() =>
        this.zitiWasmFetch(req)
      );
    } catch (err) {
      throw err; // critical: re-throw to allow upstream catch
    }

    return resp;
  }

  delay(time: number) {
    return new Promise(resolve => setTimeout(resolve, time));
  }

  /**
   *  Initialize the WASM Ziti context when the selected network has changed.
   *  Uses the session token from the NF exchange endpoint with
   *  AuthenticateWithPreviousSession to avoid the full OIDC flow.
   */
  async _initZitiWasm(steps: ConnectionStep[] = []) {
    if (isEmpty(this.settingsService?.settings?.selectedEdgeController)) {
      this.logger.warn('ZitiApiInterceptor: selectedEdgeController not set — skipping WASM init');
      return;
    }
    const selectedEdgeControllerUrl = new URL(this.settingsService.settings.selectedEdgeController);

    await this.mutex.runExclusive(async () => {
      if (!isEqual(this.currentWasmControllerHost, selectedEdgeControllerUrl.hostname)) {

        // Controller changed — wait for in-flight requests before re-init
        if (this.currentWasmControllerHost) {
          this.logger.info(`controller changed from [${this.currentWasmControllerHost}] to [${selectedEdgeControllerUrl.hostname}]. Waiting for in-flight requests...`);
          await this.httpQueueManager.waitForAllInFlight();
        }

        const networkId = this.zitiControllerService.currentNetwork?.id;
        if (isEmpty(networkId)) {
          this.logger.warn('ZitiApiInterceptor: no current network — cannot init WASM');
          return;
        }

        const controllerUrl = `${selectedEdgeControllerUrl.protocol}//${selectedEdgeControllerUrl.hostname}${selectedEdgeControllerUrl.port ? ':' + selectedEdgeControllerUrl.port : ''}`;

        // Step 0: Load WASM runtime (with download progress)
        this.updateStepStatus(steps, 0, 'active');
        this.zitiWasmService.onLoadProgress = ({ percent, receivedMB }) => {
          const step = steps[0];
          if (step) {
            step.progress = percent >= 0 ? percent : undefined;
            step.detail = percent >= 0 ? `${percent}% downloaded` : `${receivedMB.toFixed(1)} MB downloaded`;
          }
        };

        try {
          await this.zitiWasmService.loadWasm();
          this.updateStepStatus(steps, 0, 'success');
        } catch (err) {
          this.updateStepStatus(steps, 0, 'error');
          throw err;
        }

        // Step 1: Controller connection
        this.updateStepStatus(steps, 1, 'active');
        this.zitiWasmService.updateConnectionState({ controller: 'connecting', controllerUrl });

        // Reuse the console's Ziti session. Its 'auto' exchange gives an OIDC bearer token where the network supports
        // OIDC, which every controller of an HA cluster accepts, and a legacy zt-session otherwise.
        const session = this.settingsService.settings.session;
        if (isEmpty(session?.id)) {
          this.logger.warn('ZitiApiInterceptor: no Ziti session yet — cannot init WASM');
          this.updateStepStatus(steps, 1, 'error');
          this.zitiWasmService.updateConnectionState({ controller: 'error' });
          return;
        }

        this.logger.info(`ZitiApiInterceptor: connecting to ${controllerUrl} with a ${session.type} token`);

        try {
          await this.zitiWasmService.connectWithSession(controllerUrl, session.id, session.type);
          this.updateStepStatus(steps, 1, 'success');
          this.zitiWasmService.updateConnectionState({ controller: 'connected' });
        } catch (err) {
          this.updateStepStatus(steps, 1, 'error');
          this.zitiWasmService.updateConnectionState({ controller: 'error' });
          throw err;
        }

        // Step 2: Edge router connection / service availability
        this.updateStepStatus(steps, 2, 'active');
        this.zitiWasmService.setEdgeRouters([{ url: 'Discovering...', status: 'connecting' }]);

        // Requests stay on REST until a new connection is requested when any check fails
        const canDial = await this.canDialService(controllerUrl, 'nf-mgmt-service')
          && await this.hasWssEdgeRouter(controllerUrl)
          && await this.probeOverlay(controllerUrl);
        if (!canDial) {
          this.logger.info('ZitiApiInterceptor: start-up check failed — using REST until a new connection is requested');
          this.zitiWasmService.serviceAvailable = false;
          this.updateStepStatus(steps, 2, 'error');
          this.zitiWasmService.setEdgeRouters([{ url: 'No edge routers available', status: 'error' }]);
        } else {
          this.logger.info('ZitiApiInterceptor: nf-mgmt-service is available for dial');
          this.zitiWasmService.serviceAvailable = true;
          this.zitiWasmService.ready$.next(true);
          this.updateStepStatus(steps, 2, 'success');
          this.zitiWasmService.refreshEdgeRouterState();
        }

        this.currentWasmControllerHost = selectedEdgeControllerUrl.hostname;
        this.wasmFailureEncountered = false; // Reset when network changes
      }
    });
  }

  /**
   * Start or stop the WASM overlay to match the user's Ziti overlay connection setting.
   */
  private syncOverlay() {
    const enabled = this.featureService.zitiOverlayEnabled;
    if (enabled && !this.overlayActive && !isEmpty(this.overlaySessionId) && !isEmpty(this.zitiControllerService.currentNetwork?.id)) {
      this.overlayActive = true;
      this.connectOverlay();
    } else if (!enabled && this.overlayActive) {
      this.disconnectOverlay();
    }
  }

  private async connectOverlay() {
    // Each connection owns its steps, so an earlier one still running cannot write into a newer dialog
    const steps: ConnectionStep[] = [
      { label: 'Loading the WASM runtime', status: 'pending' },
      { label: 'Connecting to the network controller', status: 'pending' },
      { label: 'Connecting through an edge router', status: 'pending' },
    ];
    // Outside the network app the connection runs without the dialog; the indicator shows the result
    const dialogRef = this.inNetworkApp() ? this.openStatusDialog(steps) : null;
    try {
      await this._initZitiWasm(steps);
    } catch (err) {
      this.logger.error(`ZitiApiInterceptor: WASM init failed: ${err}`);
    } finally {
      // Release waiting requests; they fall through to REST when the service is unavailable
      this.zitiWasmService.ready$.next(true);
    }
    // Covers an init error, a failed start-up check and a missing session alike
    this.overlayFailed = !this.zitiWasmService.serviceAvailable;

    if (this.overlayCancelled) {
      await this.zitiWasmService.disconnect();
      this.zitiWasmService.updateConnectionState({ controller: 'disconnected' });
      this.zitiWasmService.setEdgeRouters([]);
      return;
    }
    if (this.overlayFailed) {
      // Stays open so the user can retry or close it
      return;
    }

    // Auto-close dialog after a short delay so user sees final state
    setTimeout(() => dialogRef?.close(), 1500);
  }

  /**
   * Use direct communication for the current network until the network or the overlay setting changes.
   */
  private cancelOverlay() {
    this.logger.info('ZitiApiInterceptor: overlay connection cancelled, using direct communication');
    this.overlayCancelled = true;
    this.zitiWasmService.serviceAvailable = false;
    this.zitiWasmService.updateConnectionState({ controller: 'disconnected' });
    this.zitiWasmService.ready$.next(true);
  }

  /**
   * A request that still fails over the overlay after its retries means the overlay stopped working, for example
   * because the controller revoked its session. Later requests use REST; a new session starts a fresh connection.
   */
  private markOverlayBroken(err: any) {
    if (!this.zitiWasmService.serviceAvailable) {
      return;
    }
    this.logger.warn(`ZitiApiInterceptor: overlay stopped working, using direct communication: ${err}`);
    this.zitiWasmService.serviceAvailable = false;
    this.zitiWasmService.updateConnectionState({ controller: 'error' });
  }

  /**
   * Starts a fresh connection with the current session when the user asks, after a failed or cancelled one.
   */
  private async retryOverlay() {
    if (!this.featureService.zitiOverlayEnabled) {
      return;
    }
    this.logger.info('ZitiApiInterceptor: retrying the overlay connection');
    await this.disconnectOverlay();
    this.syncOverlay();
  }

  private async disconnectOverlay() {
    // Cleared now, not after the mutex, so a connection requested while this waits is not undone by it
    this.overlayActive = false;
    await this.mutex.runExclusive(async () => {
      this.overlayCancelled = false;
      this.overlayFailed = false;
      this.wasmFailureEncountered = false;
      this.zitiWasmService.serviceAvailable = false;
      this.zitiWasmService.ready$.next(false);
      this.currentWasmControllerHost = '';
      await this.zitiWasmService.disconnect();
      this.zitiWasmService.resetMetrics();
    });
  }

  /**
   * True in the apps that show the connection indicator. A collapsed, unlocked side nav clears the area, so the
   * previous area applies then; no area at all keeps the dialog.
   */
  private inNetworkApp(): boolean {
    const area = this.menuService.areaId || this.menuService.lastArea;
    return isEmpty(area) || NETWORK_APP_AREAS.includes(area);
  }

  private openStatusDialog(steps: ConnectionStep[]): MatDialogRef<ZitiConnectionStatusComponent> {
    this.statusDialogRef?.close();
    const dialogRef = this.dialogForm.open(ZitiConnectionStatusComponent, {
      data: { steps },
      width: '27rem',
      maxWidth: 'calc(100vw - 2rem)',
      ariaLabelledBy: ZITI_CONNECTION_TITLE_ID,
      disableClose: true,
      autoFocus: false,
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (this.statusDialogRef === dialogRef) {
        this.statusDialogRef = null;
      }
      if (result === 'cancel') {
        this.cancelOverlay();
      } else if (result === 'retry') {
        this.retryOverlay();
      }
    });
    this.statusDialogRef = dialogRef;
    return dialogRef;
  }

  private updateStepStatus(steps: ConnectionStep[], index: number, status: 'pending' | 'active' | 'success' | 'error') {
    if (steps[index]) {
      steps[index].status = status;
    }
  }

  /**
   * Check if a service is available for dialing by querying the controller's
   * client API with the exchange session token.
   */
  private async canDialService(controllerUrl: string, serviceName: string): Promise<boolean> {
    try {
      const url = `${controllerUrl}/edge/client/v1/services?filter=name="${serviceName}"`;
      const resp: any = await this.httpClient.get(url, { headers: this.clientApiHeaders() }).toPromise();
      const services = resp?.data || [];

      if (services.length === 0) {
        this.logger.info(`canDialService: service "${serviceName}" not found`);
        return false;
      }

      const svc = services[0];
      const permissions = svc.permissions || [];
      if (!permissions.includes('Dial')) {
        this.logger.info(`canDialService: service "${serviceName}" found but no Dial permission (permissions: ${permissions})`);
        return false;
      }

      return true;
    } catch (err) {
      this.logger.info(`canDialService: failed to check service "${serviceName}": ${err}`);
      return false;
    }
  }

  /**
   * Check that the identity has an online edge router with a WSS listener; the browser can only connect over WSS.
   */
  private async hasWssEdgeRouter(controllerUrl: string): Promise<boolean> {
    try {
      const url = `${controllerUrl}/edge/client/v1/current-identity/edge-routers`;
      const resp: any = await this.httpClient.get(url, { headers: this.clientApiHeaders() }).toPromise();
      const routers = resp?.data || [];
      const usable = routers.filter((er: any) =>
        er.isOnline && Object.values(er.supportedProtocols || {}).some((addr: any) => String(addr).startsWith('wss:'))
      );
      if (usable.length === 0) {
        this.logger.info(`hasWssEdgeRouter: none of ${routers.length} edge router(s) is online with a WSS listener`, {
          routers: routers.map((er: any) => ({ name: er.name, isOnline: er.isOnline, protocols: Object.keys(er.supportedProtocols || {}) })),
        });
        return false;
      }
      return true;
    } catch (err) {
      this.logger.info(`hasWssEdgeRouter: failed to list edge routers: ${err}`);
      return false;
    }
  }

  /**
   * Send one request over the overlay to prove that an edge router connects and the management service answers.
   */
  private async probeOverlay(controllerUrl: string): Promise<boolean> {
    const probe = new HttpRequest<any>('GET', `${controllerUrl}/edge/management/v1/current-identity`, null, {
      headers: new HttpHeaders(this.clientApiHeaders()),
    });
    let probeTimer: ReturnType<typeof setTimeout>;
    const timeout = new Promise<never>((_, reject) => {
      probeTimer = setTimeout(() => reject(new Error(`no response within ${OVERLAY_PROBE_TIMEOUT_MS}ms`)), OVERLAY_PROBE_TIMEOUT_MS);
    });
    try {
      const resp = await Promise.race([this.zitiWasmFetch(probe), timeout]);
      this.logger.info(`probeOverlay: overlay answered with HTTP ${resp.status}`);
      return true;
    } catch (err) {
      this.logger.info(`probeOverlay: request over the overlay failed: ${err}`);
      return false;
    } finally {
      clearTimeout(probeTimer);
    }
  }

  private clientApiHeaders(): Record<string, string> {
    const session = this.settingsService.settings.session;
    return session.type === 'SESSION' ? { 'zt-session': session.id } : { Authorization: `Bearer ${session.id}` };
  }

  /**
   *  Do an HTTP fetch, over Ziti WASM, to a protected resource
   */
  async zitiWasmFetch( request: HttpRequest<any>): Promise<HttpResponse<any>> {

    // Serialize the Angular request to raw HTTP/1.1
    const rawRequest = serializeHttpRequest(request);
    const startTime = performance.now();
    const bytesSent = new TextEncoder().encode(rawRequest).length;

    this.logger.info(`[ziti] → ${request.method} ${request.url} (${bytesSent}B)`, {
      headers: redactHeaders(angularHeadersToObject(request.headers)),
      body: request.body,
    });

    // Dial the management service and get the raw response
    let rawResponse: string;
    try {
      rawResponse = await this.zitiWasmService.dialService(
        'nf-mgmt-service',
        rawRequest,
        (chunk: string) => {}
      );
    } catch (err) {
      const durationMs = performance.now() - startTime;
      this.zitiWasmService.recordRequest(bytesSent, 0, durationMs, true);
      this.logger.error(`[ziti] ✗ ${request.method} ${request.url} failed after ${durationMs.toFixed(1)}ms`, err);
      throw err;
    }

    const durationMs = performance.now() - startTime;
    const bytesReceived = new TextEncoder().encode(rawResponse).length;
    this.zitiWasmService.recordRequest(bytesSent, bytesReceived, durationMs, false);

    // Parse the raw HTTP response
    const parsed = parseRawHttpResponse(rawResponse);
    let responseBodyForLog: any = parsed.body;
    try { responseBodyForLog = JSON.parse(parsed.body); } catch { /* keep as string */ }
    this.logger.info(`[ziti] ← ${parsed.status} ${request.method} ${request.url} (${bytesReceived}B, ${durationMs.toFixed(1)}ms)`, {
      status: parsed.status,
      statusText: parsed.statusText,
      headers: redactHeaders(parsed.headers),
      body: responseBodyForLog,
    });

    // Parse the body based on response type
    let parsedBody: any;
    switch (request.responseType) {
      case 'text':
        parsedBody = parsed.body;
        break;
      case 'json':
      default:
        try {
          parsedBody = JSON.parse(parsed.body);
        } catch {
          parsedBody = parsed.body;
        }
        break;
    }

    // Transform into an Angular-compatible response
    const angularResponse = new HttpResponse({
      body: parsedBody,
      status: parsed.status,
      statusText: parsed.statusText,
      headers: new HttpHeaders(parsed.headers),
      url: request.url,
    });

    return angularResponse;
  }

  /**
   *  Decide if an HTTP fetch should go over Ziti, or not
   */
  shouldRouteOverZiti( req: HttpRequest<any> ): boolean /* Promise<Observable<HttpEvent<any>>> */ {

    if (isEmpty(this.settingsService?.settings?.selectedEdgeController)) {
      return false;
    }
    // Set up filters by target hostname and target path
    const selectedEdgeControllerUrl = new URL(this.settingsService.settings.selectedEdgeController);
    const selectedEdgeControllerHost = selectedEdgeControllerUrl.hostname;
    const urlHost = new URL(req.url).host;
    // Recognize both edge-management and fabric APIs on the selected controller
    // so fabric calls (e.g., the service path visualizer) flow through
    // addAuthToken and pick up the Ziti session header instead of leaking out
    // unauthenticated and falling back to an NF bearer.
    const isApiPath = req.url.includes('/edge/management/v1') || req.url.includes('/fabric/');

    // Skip Ziti if not matching host/path
    if (!isEqual(urlHost, selectedEdgeControllerHost) || !isApiPath) {
      return false;
    }

    // We matched the controller host/path, so route over Ziti
    return true;
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Bypass the request if is NOT targeting controller mgmt API
    if (!this.shouldRouteOverZiti( req )) {
      return next.handle(req);
    }

    if (this.interceptZitiIdentitiesRequest(req, this.zitiControllerService.nfAdminAuthPolicyId.value, true)) {
      const externalEvent$: Observable<any> = this.zitiControllerService.nfAdminAuthPolicyId;
      return externalEvent$.pipe(
        filter(authPolicyId => !isEmpty(authPolicyId)),
        take(1),
        switchMap(() => {
          // Now that the ID is available, continue with the rest of the intercept logic
          return this.doIntercept(req, next);
        })
      );
    }
    return this.doIntercept(req, next);
  }

  doIntercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    req = this.addAuthPolicyId(req);
    req = this.addNFManagedFilter(req);

    // Bypass the request if we do not have a Ziti session yet
    if (isEmpty(this.settingsService?.settings?.session) || isEmpty(this.settingsService?.settings?.selectedEdgeController)) {
      return next.handle(req);
    }

    // Use plain REST unless the user turned on the Ziti overlay connection (beta only)
    // and did not cancel it for this network
    this.syncOverlay();
    if (!this.featureService.zitiOverlayEnabled || this.overlayCancelled) {
      return this.handleRestRequest(req, next);
    }

    // Bypass the request if is NOT targeting controller mgmt API
    if (!this.shouldRouteOverZiti( req )) {
      return next.handle(req);
    }

    // Wait for WASM to be fully initialized before routing over Ziti
    if (!this.zitiWasmService.serviceAvailable) {
      return this.zitiWasmService.ready$.pipe(
        filter(ready => ready),
        take(1),
        switchMap(() => {
          // If init failed and service is still unavailable, fall through to REST
          if (!this.zitiWasmService.serviceAvailable) {
            return this.handleRestRequest(req, next);
          }
          return this.doIntercept(req, next);
        })
      );
    }

    this.logger.info(`intercept entered for ${req.url}`);

    req = this.addAuthToken(req);

    // If the selected network changed, we need to reset the error indicator (which may have tripped on the previously-selected network).
    // This allows us to initially attempt Ziti connections on requests for the newly-selected network.
    const selectedEdgeControllerUrl = new URL(this.settingsService.settings.selectedEdgeController);
    if (!isEqual(this.currentWasmControllerHost, selectedEdgeControllerUrl.hostname)) {
      this.wasmFailureEncountered = false;
    }

    // Ziti execution path
    const queued$ = from(
      this.ngZone.run(() => this.doAsyncWork(req))
    ).pipe(

      concatMap((event: HttpEvent<any>) => of(event)),
      retryWhen(errors =>
        errors.pipe(
          mergeMap((err, retryCount) => {
            const maxRetries = 3;
            this.logger.info(
              `retryWhen entered for ${req.url} retryCount[${retryCount}] error: ${err}`
            );

            if (retryCount < maxRetries) {
              return timer(500);
            }

            // propagate error after max retries
            return throwError(() => err);
          })
        )
      ),

      // Final fallback to REST if Ziti fails; the REST path also replaces a session the controller rejected
      catchError(err => {
        this.logger.info(`fallback to native HTTP for ${req.url}`);
        this.markOverlayBroken(err);
        return this.handleRestRequest(req, next);
      }),

      // Share execution between queue + caller
      shareReplay({ bufferSize: 1, refCount: false })
    );

    // Queue the request
    this.httpQueueManager.enqueue(queued$);

    // Return the observable to original caller
    return queued$;
  }

  /**
   * Send the request over plain HTTP with the Ziti session token.
   */
  private handleRestRequest(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    req = this.addAuthToken(req);
    return next.handle(req).pipe(
      switchMap((event: HttpEvent<any>) => {
        if (event instanceof HttpResponse && this.shouldBeIntercepted(event)) {
          const paging = {
            searchOn: 'name',
            filter: '',
            total: 1,
            page: 1,
            sort: 'name',
            order: 'asc'
          };
          return this.getZitiData('identities', paging).pipe(
            switchMap((secondResponse: any) => {
              event.body.data.identities = secondResponse.meta.pagination.totalCount;
              return of(event);
            })
          );
        }
        return of(event);
      }),
      catchError((error: any) => this.retryWithFreshSession(error, req, next))
    );
  }

  // A stale/revoked zt-session 401s with no recovery; re-mint the session once and retry.
  private retryWithFreshSession(error: any, req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (error?.status !== 401 || req.headers.has('X-Ziti-Session-Retry')) {
      return throwError(() => error);
    }
    // Don't re-mint when the user isn't authenticated; let the 401 surface rather than
    // pop the controller modal on the login screen.
    if (!this.auth0Service?.authenticated) {
      return throwError(() => error);
    }
    // Don't re-mint before a network is selected; minting against an undefined id 400s
    // (networks/undefined/exchange) and pops the controller modal on init.
    if (isEmpty(this.zitiControllerService.currentNetwork?.id)) {
      return throwError(() => error);
    }
    // Skip if a mint is already in flight (e.g. the currentNetwork bootstrap); the request waits for it below.
    if (!this.zitiControllerService.loadingZitiController) {
      if (Date.now() - this.lastSessionRemintAt < SESSION_REMINT_COOLDOWN_MS) {
        return throwError(() => error);
      }
      this.lastSessionRemintAt = Date.now();
      this.zitiControllerService.getZitiControllerSession();
    }
    return this.zitiControllerService.zitiSettings.pipe(
      filter((session: any) => !isEmpty(session?.zitiSessionId)),
      take(1),
      timeout(15000),
      switchMap(() => {
        const retried = this.addAuthToken(req.clone({ setHeaders: { 'X-Ziti-Session-Retry': '1' } }));
        return next.handle(retried);
      })
    );
  }

  private shouldBeIntercepted(event) {
    return event.url.indexOf('edge/management/v1/summary') >= 0;
  }

  private addAuthToken(request: HttpRequest<any>) {
    if (request.url.indexOf(this.settingsService?.settings?.selectedEdgeController) < 0) {
      return request;
    }

    const session = this.settingsService.settings.session;
    if(session.type === 'SESSION') {
      return request.clone({ setHeaders: { 'zt-session': session.id } });
    } else {
      return request.clone({ setHeaders: { Authorization: `Bearer ${session.id}`} });
    }
  }

  private addNFManagedFilter(request: HttpRequest<any>) {
    const url = request.url;
    if (this.interceptNFManagedRequest(request)) {
      const managedFilter = '(tags.managed != "NetFoundry" and tags.managed != "netfoundry")';
      let filter: any = request.params.get('filter')?.toString();
      if (isEmpty(filter)) {
        const urlParams = new URLSearchParams(new URL(url).search);
        filter = urlParams.get('filter')?.toString();
        if (isEmpty(filter)) {
          filter = managedFilter;
          request = request.clone({
            params: request.params.set('filter', filter)
          });
        } else {
          filter += ` and ${managedFilter}`;
          filter = filter.replace('name contains "%" and ', '');
          filter = filter.replace('name contains "%"', '');
          request = request.clone({
            url: this.setURLParam(url, 'filter', filter)
          });
        }
      } else if (filter.indexOf(managedFilter) === -1) {
        filter += ` and ${managedFilter}`;
        request = request.clone({
          params: request.params.set('filter', filter)
        });
      }
    }
    return request;
  }

  private addAuthPolicyId(request: HttpRequest<any>) {
    const url = request.url;
    const authPolicyId = this.settingsService.settings.nfAdminAuthPolicyId;
    if (this.interceptZitiIdentitiesRequest(request, authPolicyId)) {
      let filter: any = request.params.get('filter')?.toString();
      if (isEmpty(filter)) {
        const urlParams = new URLSearchParams(new URL(url).search);
        filter = urlParams.get('filter')?.toString();
        if (isEmpty(filter)) {
          filter = `authPolicyId != "${authPolicyId}"`;
          request = request.clone({
            params: request.params.set('filter', filter)
          });
        } else {
          filter += ` and authPolicyId != "${authPolicyId}"`;
          filter = filter.replace('name contains "%" and ', '');
          filter = filter.replace('name contains "%"', '');
          request = request.clone({
            url: this.setURLParam(url, 'filter', filter)
          });
        }
      } else if (filter.indexOf('authPolicyId') === -1) {
        filter += ` and authPolicyId != "${authPolicyId}"`;
        request = request.clone({
          params: request.params.set('filter', filter)
        });
      }
    }
    return request;
  }

  private interceptZitiIdentitiesRequest(request, authPolicyId, ignoreEmpty = false) {
    if (this.featureService.showManagedEntities) {
      return;
    }
    const url = request.url;
    const urlWithoutParams = url.split('?')[0];
    const matchString = 'edge/management/v1/identities';
    if (request.method !== 'GET' || (isEmpty(authPolicyId) && !ignoreEmpty) || urlWithoutParams.indexOf(matchString) < 0) {
      return false;
    }
    const urlLength = urlWithoutParams.length;
    const matchStringLength = matchString.length;
    const maxMatchStringIndexPlusLength = urlWithoutParams.indexOf(matchString) + matchStringLength + 1;
    return maxMatchStringIndexPlusLength >= urlLength;
  }

  private interceptNFManagedRequest(request) {
    if (this.featureService.showManagedEntities) {
      return false;
    }
    const url = request.url;
    const urlWithoutParams = url.split('?')[0];

    const matchStrings = [
      'edge/management/v1/identities',
      'edge/management/v1/services',
      'edge/management/v1/edge-routers',
      'edge/management/v1/service-policies',
      'edge/management/v1/edge-router-policies',
      'edge/management/v1/service-edge-router-policies',
      'edge/management/v1/configs'
    ];
    let urlMatched = false;
    let matchedString;
    matchStrings.forEach((matchString) => {
      if (urlWithoutParams.indexOf(matchString) >= 0) {
        matchedString = matchString;
        urlMatched = true;
      }
    });
    if (request.method !== 'GET' || !urlMatched) {
      return false;
    }
    const urlLength = urlWithoutParams.length;
    const matchedStringLength = matchedString.length;
    const maxMatchStringIndexPlusLength = urlWithoutParams.indexOf(matchedString) + matchedStringLength + 1;
    return maxMatchStringIndexPlusLength >= urlLength;
  }

  setURLParam(url: any, param: any, value: any) {
    const urlObj = new URL(url);
    const searchParams = urlObj.searchParams;
    searchParams.set(param, value);
    return urlObj.toString();
  }

  getZitiData(type: string, paging: any) {
    const apiVersions = this.settingsService.apiVersions || {};
    const prefix = apiVersions["edge-management"]?.v1?.path || '/edge/management/v1';
    const url = this.settingsService.settings.selectedEdgeController;
    const serviceUrl = url + prefix + "/" + type;

    return this.httpClient.get(serviceUrl,{}).pipe(
      catchError((err: any) => {
        const error = "Server Not Accessible";
        this.logger.error('Error getting identities for summary');
        throw(err);
      }),
      map((results: any) => {
        return results;
      })
    );
  }
}
